/**
 * OVERVOLT — central tunables.
 *
 * Every gameplay and engine number lives here so balance can change in one
 * place. The object is intentionally *mutable*: it is exposed live at
 * `window.__GAME__.config` for tests and balancing playgrounds.
 */

/**
 * The eight enemy ("Glitch") archetypes. Sprint 31 added **Streak** — the
 * fast, slow-resistant lane-runner specified in `docs/ENEMY_DESIGN.md`, whose
 * mechanics deliberately invalidate a single-kill-zone build (REC 5 of
 * `docs/STRATEGY_ANALYSIS.md` §7).
 */
export type EnemyType =
  | 'bit'
  | 'packet'
  | 'daemon'
  | 'fork'
  | 'cipher'
  | 'patch'
  | 'buffer'
  | 'streak';

/**
 * The seven placeable types. Pulse / Arc / Cryo / Mortar / Sniper are weapon
 * towers; the Pylon is power infrastructure; the **Wall** is an inert,
 * gun-less, power-free structural blocker for Maze Mode (`weapon: false`,
 * never upgraded — see docs/GAME_DESIGN.md §16.9). They share the placement /
 * sell / blocked-tile pipeline, so "tower" below means any of the seven.
 */
export type TowerType = 'pulse' | 'arc' | 'pylon' | 'cryo' | 'mortar' | 'sniper' | 'wall';

/**
 * Any spawnable unit — the seven roster archetypes plus the Kernel boss
 * (`'kernel'`, see docs/GAME_DESIGN.md §17). The boss is deliberately *not* in
 * the `EnemyType` roster (it has no `config.enemies` entry — its tuning lives
 * in the dedicated `boss*` constants), so this wider union types `Enemy.type`
 * and `WaveGroup.type` without touching the seven-type roster records.
 */
export type SpawnType = EnemyType | 'kernel';

/**
 * Enemy adds spawned at a Kernel phase transition — see docs/GAME_DESIGN.md
 * §17.2. A sparse map of roster type → count.
 */
export type BossAddSpec = Partial<Record<EnemyType, number>>;

/** The three difficulty modes. */
export type Difficulty = 'easy' | 'normal' | 'hard';

/**
 * Per-tower target-selection mode — see docs/GAME_DESIGN.md §5 Targeting.
 * Every weapon tower carries one; the player cycles it with `T` / the panel.
 */
export type TargetMode = 'first' | 'last' | 'strongest' | 'weakest' | 'nearest';

/** The three mid-wave active abilities — see docs/GAME_DESIGN.md §15. */
export type AbilityId = 'overcharge' | 'airstrike' | 'empBurst';

/** A run's mode — Campaign / Endless / Maze. Mirrors `GameMode` in game/state.ts. */
export type RunMode = 'campaign' | 'endless' | 'maze';

/** The four branches of the meta-progression unlock tree — see §19.2. */
export type UnlockBranch = 'arsenal' | 'modes' | 'challenge' | 'cosmetic';

/**
 * What a purchased unlock node grants — a discriminated union the UI-only gate
 * consumes to decide what a profile may select. The simulation never reads it
 * (the gate lives purely in the title screen / HUD). See docs/GAME_DESIGN.md §19.
 */
export type UnlockGrant =
  | { kind: 'tower'; tower: TowerType }
  | { kind: 'ability'; ability: AbilityId }
  | { kind: 'mode'; mode: 'endless' | 'maze' }
  | { kind: 'difficulty'; difficulty: Difficulty }
  | { kind: 'overload'; tier: number }
  | { kind: 'theme'; theme: string };

/**
 * One node of the meta-progression unlock tree — see docs/GAME_DESIGN.md §19.2.
 * A node is *owned* once its `id` is in `profile.unlocked`; a purchase needs
 * `flux ≥ cost` and every `prereq` already owned.
 */
export interface UnlockNode {
  /** Stable id — also the `unlock(id)` test-API key (e.g. `tower.mortar`). */
  id: string;
  /** Human-readable name for the Profile screen. */
  name: string;
  /** Which branch column the node sits in. */
  branch: UnlockBranch;
  /** Flux cost to purchase. */
  cost: number;
  /** Id of a node that must be owned first, or undefined for a root node. */
  prereq?: string;
  /** What owning the node enables (consumed by the UI-only gate). */
  grants: UnlockGrant;
}

/**
 * One wave-intensity tier for the build-phase Overload dial — see
 * docs/GAME_DESIGN.md §8 Overload. Tier 0 is balance-neutral (all 1.0).
 */
export interface OverloadTier {
  /** Display name for the HUD (e.g. "Nominal", "Meltdown"). */
  name: string;
  /** Enemy effective-HP multiplier applied to the wave. */
  hpMult: number;
  /** Enemy travel-speed multiplier applied to the wave. */
  speedMult: number;
  /** Enemy-count multiplier applied to the wave. */
  countMult: number;
  /** Per-kill + wave-clear gold reward multiplier applied to the wave. */
  goldMult: number;
  /** Per-kill + wave-clear + Endless-depth score multiplier applied to the wave. */
  scoreMult: number;
}

/**
 * Static definition for one active ability — see docs/GAME_DESIGN.md §15.
 * Sprint 20 (REC 6 re-role) re-shapes each ability around a *proactive*
 * optimal use, so the type carries a handful of new fields per ability:
 *
 *  - **Overcharge** also runs an **aftershock brownout** after its burst —
 *    `aftershockTicks` ticks at a `aftershockEfficiency` cap on the cooldown-
 *    scaling efficiency. The proactive cost the player builds the burst
 *    around.
 *  - **Airstrike** leaves a **scorch zone** for `scorchTicks` after the
 *    initial blast — every tick each living enemy in the zone takes
 *    `scorchTickDamage × hpMultiplier(wave)` armour-piercing chip damage
 *    and is brittle-marked at `scorchBrittleAmp` for `scorchBrittleTicks`.
 *  - **EMP Burst** also applies a **brittle field** (`empBrittleAmp` for
 *    `empTicks`) and awards a per-stunned-enemy score bonus of
 *    `round(empScoreBonusPer × overloadTiers[waveOverload].scoreMult)` —
 *    the Overload synergy.
 *
 * Effect-specific fields are 0 (or 1.0 for amp multipliers) for abilities
 * that do not use them, so every value still type-checks as a `number`.
 */
export interface AbilityDef {
  /** Stable id — also the `useAbility` test-API key. */
  id: AbilityId;
  /** Human-readable name for the HUD. */
  name: string;
  /** Gold cost per use. */
  cost: number;
  /** Cooldown in ticks after a use (counts down only during the wave phase). */
  cooldownTicks: number;
  /** Overcharge: ticks the boosted-efficiency window lasts (0 otherwise). */
  durationTicks: number;
  /** Overcharge: the floor grid efficiency while the window is active (0 otherwise). */
  efficiency: number;
  /**
   * Overcharge: ticks the **aftershock brownout** lasts immediately after the
   * burst window ends (0 otherwise). During the aftershock the cooldown-
   * scaling efficiency (§6) is *capped* at `aftershockEfficiency` — the
   * proactive cost of the burst (docs/GAME_DESIGN.md §15).
   */
  aftershockTicks: number;
  /**
   * Overcharge: the *cap* on grid efficiency during the aftershock brownout
   * (0 otherwise). A value below 1.0 — every tower fires slower than normal
   * for the aftershock window.
   */
  aftershockEfficiency: number;
  /** Airstrike: blast radius in tiles (0 otherwise). */
  radius: number;
  /** Airstrike: base damage before per-wave `hpMultiplier` scaling (0 otherwise). */
  damage: number;
  /**
   * Airstrike: ticks the placed **scorch zone** lingers after the initial
   * blast (0 otherwise). The zone occupies the same `radius` as the blast.
   * See docs/GAME_DESIGN.md §15.
   */
  scorchTicks: number;
  /**
   * Airstrike: per-tick chip damage dealt by the scorch zone to every enemy
   * inside it, before `hpMultiplier(wave)` scaling (0 otherwise).
   */
  scorchTickDamage: number;
  /**
   * Airstrike: brittle-mark amp applied (refresh-on-reapply, max wins) to
   * every enemy standing in the scorch zone (1.0 otherwise — inert).
   */
  scorchBrittleAmp: number;
  /**
   * Airstrike: brittle-mark duration in ticks each scorch tick refreshes onto
   * an enemy still in the zone (0 otherwise). Tuned slightly longer than the
   * scorch's own per-tick cadence so the mark never flickers off between
   * pulses.
   */
  scorchBrittleTicks: number;
  /** EMP Burst: ticks the field-wide EMP stun lasts (0 otherwise). */
  empTicks: number;
  /**
   * EMP Burst: brittle-mark amp applied to every enemy alive on the field
   * for `empTicks` (1.0 otherwise — inert). The Cryo painter's stronger
   * brittle wins (refresh-on-reapply, `max` amp). See §15.
   */
  empBrittleAmp: number;
  /**
   * EMP Burst: per-stunned-enemy base score bonus awarded immediately on
   * fire, before `overloadTiers[waveOverload].scoreMult` scaling (0
   * otherwise). The proactive Overload-synergy lever.
   */
  empScoreBonusPer: number;
}

/**
 * Per-difficulty multipliers, applied over the Normal base config. Normal is
 * the base — all its multipliers are 1.0. See docs/GAME_DESIGN.md §8.
 */
export interface DifficultyDef {
  /** Enemy effective-HP multiplier. */
  enemyHpMult: number;
  /** Wave enemy-count multiplier (campaign group counts; Endless budget). */
  enemyCountMult: number;
  /** Enemy travel-speed multiplier. */
  enemySpeedMult: number;
  /** Starting-gold multiplier (resolved to an integer at run-init). */
  startingGoldMult: number;
  /** Starting-lives multiplier (resolved to an integer at run-init). */
  startingLivesMult: number;
  /** Gold-from-kill multiplier; score always uses the raw bounty. */
  bountyMult: number;
}

/** Static stat block for an enemy archetype. */
export interface EnemyDef {
  /** Human-readable name for the HUD. */
  label: string;
  /** Base hit points before per-wave and difficulty scaling. */
  hp: number;
  /** Travel speed in path-tiles per second. */
  speed: number;
  /** Gold + score awarded on kill (the Normal baseline). */
  bounty: number;
  /** Lives drained from the Core if it leaks through. */
  lifeDmg: number;
  /** Neon fill color. */
  color: string;
  /** Render radius in pixels. */
  radius: number;
  /** Base flat per-hit damage reduction, before Corrosion shred. */
  baseArmor: number;
  /** Base shield pool before per-wave / difficulty scaling (0 = no shield). */
  baseShield: number;
  /** Ticks after taking damage before the shield begins to regenerate. */
  shieldRegenDelay: number;
  /** Shield HP regained per tick once regeneration has started. */
  shieldRegenRate: number;
}

/** Stats shared by every tower level. */
export interface TowerLevelBase {
  /** Damage per shot. */
  damage: number;
  /** Targeting range in tiles (0 for non-weapons). */
  range: number;
  /** Base cooldown between shots, in logical ticks (0 for non-weapons). */
  cooldownTicks: number;
  /** Power demand drawn from the grid (0 for non-weapons). */
  powerDraw: number;
}

/** A Pulse Turret level — single-target hitscan; no extra stats. */
export type PulseLevel = TowerLevelBase;

/** A Sniper Rail level — line-of-fire pierce lance (§5); no extra stats. */
export type SniperLevel = TowerLevelBase;

/**
 * A Wall level — the dedicated Maze-Mode blocker (docs/GAME_DESIGN.md §16.9).
 * An inert structure: it has no weapon stats at all (damage / range / cooldown
 * / power draw are all 0), and it is never upgraded, so its single level is
 * the only one that matters.
 */
export type WallLevel = TowerLevelBase;

/** An Arc Coil level. */
export interface ArcLevel extends TowerLevelBase {
  /** Max enemies a chain hits, including the primary. */
  chainTargets: number;
}

/**
 * A Cryo Emitter level — see docs/GAME_DESIGN.md §5 Cryo. The re-roled Cryo is
 * a **tight-radius chokepoint painter**: a small `range` and small `aoeTargets`
 * mean each pulse paints only the tile-plus-neighbours pinch right at the
 * emitter, and every painted enemy gains BOTH a slow AND the new brittle
 * damage-vulnerability mark — every other tower's incoming damage is scaled
 * by `brittleAmp` against that enemy for `brittleDuration` ticks. Both
 * statuses *refresh-on-reapply* (slow `min`, brittle `max`), so overlapping
 * Cryos cannot rebuild the old board-wide multiplier by stacking.
 */
export interface CryoLevel extends TowerLevelBase {
  /** Slow speed multiplier applied to hit enemies. */
  slowFactor: number;
  /** Slow duration in ticks. */
  slowDuration: number;
  /** Max enemies one AoE pulse hits. */
  aoeTargets: number;
  /**
   * Damage-vulnerability multiplier applied to a brittle-marked enemy —
   * `damageOut = damageIn × brittleAmp` before armor / shield resolution.
   * `1.0` is no amplification; the painter sits at `1.20 / 1.30 / 1.40`.
   */
  brittleAmp: number;
  /** Brittle-mark duration in ticks (tuned to slightly exceed `slowDuration`). */
  brittleDuration: number;
}

/** A Splash Mortar level. */
export interface MortarLevel extends TowerLevelBase {
  /** Blast radius in tiles around the detonation point. */
  blastRadius: number;
  /**
   * Minimum (dead-zone) range in tiles — see docs/GAME_DESIGN.md §5. The
   * Mortar lobs shells in a steep arc and **cannot** target an enemy closer
   * than this; it fires only at targets in the firing annulus between
   * `minRange` and `range`, so it must be placed back from the path.
   */
  minRange: number;
}

/** A Pylon level. */
export interface PylonLevel extends TowerLevelBase {
  /** Power supply contributed to the grid. */
  supply: number;
}

/**
 * Static definition for a tower type, including its 3-level upgrade table.
 * Parameterised by the tower's level stat type so each tower's fields are
 * mandatory and foreign fields are rejected.
 */
export interface TowerDef<L extends TowerLevelBase> {
  /** Human-readable name for the HUD. */
  label: string;
  /** Neon accent color. */
  color: string;
  /** True for towers that fire; false for the Pylon. */
  weapon: boolean;
  /** Gold cost to build (places the tower at level 1). */
  cost: number;
  /** Per-level stats; index 0 = level 1, index 2 = level 3. */
  levels: [L, L, L];
  /** Gold to upgrade: index 0 = level 1→2, index 1 = level 2→3. */
  upgradeCosts: [number, number];
  /** Targeting mode a freshly-built tower starts in (defaults to `first`). */
  defaultTargetMode?: TargetMode;
  /** When true, this tower's shots ignore enemy `armor` entirely. */
  armorPiercing?: boolean;
  /**
   * When `false` the placeable can never be upgraded — the dedicated **Wall**
   * (docs/GAME_DESIGN.md §16.9), an inert structural blocker with nothing to
   * level up. Omitted / `true` for every weapon tower and the Pylon.
   */
  upgradable?: boolean;
}

/** The seven placeable definitions, each with its own level stat type. */
export interface TowersConfig {
  pulse: TowerDef<PulseLevel>;
  arc: TowerDef<ArcLevel>;
  cryo: TowerDef<CryoLevel>;
  mortar: TowerDef<MortarLevel>;
  sniper: TowerDef<SniperLevel>;
  pylon: TowerDef<PylonLevel>;
  wall: TowerDef<WallLevel>;
}

/** One sequential spawn group inside a wave. */
export interface WaveGroup {
  /** Spawn type — a roster archetype, or `'kernel'` for the boss group (§17). */
  type: SpawnType;
  count: number;
  /** Ticks between consecutive spawns (at 1x speed). */
  interval: number;
}

/**
 * Stable id for a per-wave Endless **modifier** (Sprint 47). Mirrors
 * `EndlessModifierId` in `src/game/endless.ts`; declared here so `WaveDef`
 * can carry the id without pulling the runtime module into the config tree.
 */
export type WaveModifierId =
  | 'armoredSurge'
  | 'fastPack'
  | 'scatteredSpread'
  | 'streakRunners'
  | 'daemonRush';

/** A full wave: groups spawn sequentially. */
export interface WaveDef {
  groups: WaveGroup[];
  /**
   * Endless per-wave **modifier** id (Sprint 47) — present on every Endless
   * wave from `endlessModifierStartWave` onwards *except* surge / boss-
   * milestone waves. Always `undefined` for Campaign waves (1–10) and for
   * surge / boss waves — preserved across save/resume by re-deriving from
   * `(seed, wave)`. See docs/ENDLESS_DESIGN.md.
   */
  modifier?: WaveModifierId;
}

/**
 * Map registry entry (Sprint 21) — see docs/GAME_DESIGN.md §3. The actual
 * path / blocked-tile data lives in `src/game/map.ts`; this registry is the
 * UI-visible list (order + the default), so the title-screen chip / the
 * `?map=<id>` URL param can cycle through what is available. Each `id` must
 * match a key in `MAPS` (`src/game/map.ts`).
 */
export interface MapRegistryEntry {
  /** Stable id (URL param + run save + test API). */
  id: string;
  /** Human-readable label for the title-screen chip. */
  label: string;
  /** One-line tagline (chip hover / docs). */
  tagline: string;
}

/** Shape of the whole config tree. */
export interface GameConfig {
  canvas: { width: number; height: number };
  loop: { updateHz: number; maxStepsPerFrame: number; maxFrameDelta: number };
  /** Height of the HUD strip above the playfield, in pixels. */
  hudHeight: number;
  grid: { cols: number; rows: number; tileSize: number };
  /** Logical simulation ticks per second at 1x speed. */
  tickRate: number;
  /** Multiplier applied by fast-forward. */
  fastForward: number;
  /**
   * Number of scripted Campaign waves — the Campaign wins on clearing this
   * wave, and Endless/Maze re-anchor their procedural generator here. Always
   * equal to `waves.length`. See docs/GAME_DESIGN.md §8 / §14.
   */
  campaignLength: number;
  /** Default deterministic PRNG seed. */
  seed: number;
  /** Default difficulty for a fresh run. */
  difficulty: Difficulty;
  /** Whether the first-run onboarding aids are active (UI-only; off in tests). */
  onboardingEnabled: boolean;
  /** Starting gold (the Normal base; scaled by the difficulty multiplier). */
  startingGold: number;
  /** Starting lives (the Normal base; scaled by the difficulty multiplier). */
  startingLives: number;
  /** Fraction of total spent refunded on sell. */
  sellRefundRate: number;
  waveBonusBase: number;
  waveBonusPerWave: number;
  /** Score granted per wave cleared. */
  waveClearScore: number;
  /** Score bonus per surviving life at game end. */
  livesScoreBonus: number;
  /** Score multiplier for a flawless (no lives lost) win. */
  winFlourishMultiplier: number;
  /**
   * Maze Mode route baseline — the ~straight "gauntlet" route length (in
   * segments) past which the maze-length wave-clear bonus starts paying out.
   * An open 20-tile field routes spawn→Core in 19 segments. See §16.7.
   */
  mazeBaselineLength: number;
  /**
   * Maze Mode wave-clear **score** awarded per route segment beyond
   * `mazeBaselineLength` — the primary reward that makes a long winding maze a
   * real strategic choice rather than a pure efficiency loss (§16.7).
   */
  mazeBonusScorePerTile: number;
  /**
   * Maze Mode wave-clear **gold** awarded per route segment beyond
   * `mazeBaselineLength` — a deliberately modest trickle; the gauntlet stays
   * the gold-efficient option (§16.7).
   */
  mazeBonusGoldPerTile: number;
  /**
   * Flat extra starting gold for a **Maze** run — the early-economy nudge
   * (docs/GAME_DESIGN.md §16.9). A Maze player must pay for the maze (Walls)
   * *and* the guns; without this they can afford only one in the early game,
   * which collapses the mode back to the straight gauntlet. Added on top of
   * the difficulty-scaled base, so it is mode-specific and difficulty-neutral.
   * Deliberately modest — it bootstraps the opening detour, it does not
   * trivialise the run.
   */
  mazeStartGoldBonus: number;
  /** Base grid supply provided by the reactor. */
  reactorBasePower: number;
  /**
   * Linear term of the compounding per-wave HP curve — see docs/GAME_DESIGN.md
   * §8. `hpMultiplier(w) = 1 + hpGrowthLinear*(w-1) + hpGrowthQuad*(w-1)^2`.
   */
  hpGrowthLinear: number;
  /** Quadratic term of the compounding per-wave HP curve (see `hpGrowthLinear`). */
  hpGrowthQuad: number;
  /** Wave at/after which the late-wave enemy speed ramp begins (no ramp below it). */
  lateSpeedRampStart: number;
  /** Speed added per wave beyond `lateSpeedRampStart` (e.g. +0.03 ⇒ 1.03×, 1.06×, …). */
  lateSpeedRampPerWave: number;
  /** Hard cap on the late-wave speed ramp (keeps Endless bounded). */
  lateSpeedRampMax: number;
  /** The Kernel boss's base HP before per-wave / difficulty / Overload scaling (§17). */
  bossBaseHp: number;
  /**
   * Cap on the per-wave `hpMultiplier(w)` factor applied to the Kernel's HP
   * (§17.4). Every other enemy rides the uncapped §8 quadratic curve, but an
   * Endless-milestone Kernel (waves 20 / 30 / 40 …) would otherwise balloon
   * into a pure unkillable survival wall — so the boss's wave-scaling is
   * clamped here (≈ hpMultiplier(15)), keeping every milestone a
   * hard-but-killable fight. Affects the `kernel` type only.
   */
  bossHpWaveMultCap: number;
  /** Gold + score bounty for killing the Kernel boss. */
  bossBounty: number;
  /** Lives drained from the Core if the Kernel leaks through. */
  bossLifeDmg: number;
  /** The Kernel's flat armor — 0, so Corrosion is inert on it. */
  bossArmor: number;
  /**
   * Kernel slow resistance — an incoming slow `factor` is softened to
   * `1 − (1 − factor) × (1 − bossSlowResist)`, so Cryo helps but cannot freeze
   * the boss fight (docs §17.1).
   */
  bossSlowResist: number;
  /** HP fraction at/below which the Kernel enters phase 2 (Fork Bomb). */
  bossPhase2HpFrac: number;
  /** HP fraction at/below which the Kernel enters phase 3 (Kernel Panic). */
  bossPhase3HpFrac: number;
  /** Per-phase Kernel travel speed in tiles/s (index = phase − 1). */
  bossPhaseSpeed: [number, number, number];
  /** Per-phase ticks between Power-Drain charges (index = phase − 1). */
  bossDrainInterval: [number, number, number];
  /** Telegraph ticks the Power Drain visibly charges before it emits. */
  bossDrainTelegraph: number;
  /** Ticks an emitted Power Drain throttles the grid. */
  bossDrainDuration: number;
  /** Grid base-efficiency multiplier while a Power Drain is active (forced brownout). */
  bossDrainMult: number;
  /** Enemy adds spawned when the Kernel enters phase 2. */
  bossPhase2Adds: BossAddSpec;
  /** Enemy adds spawned when the Kernel enters phase 3. */
  bossPhase3Adds: BossAddSpec;
  /**
   * Tiles ahead of the boss along the shared route at which phase-entry adds
   * spawn (Sprint 42 Kernel rework, `docs/KERNEL_REWORK_DESIGN.md` §3.2
   * Change 5). Breaks the one-tick co-line that let a Sniper lance one-shot
   * the pod and steps the pod out of any Mortar inner-annulus the boss is
   * sitting on. Clamped to `[0, pathLength)` per route at spawn time. In Maze
   * Mode the value is **not** applied (the boss-tile A* re-path already drops
   * the pod at `pathDist 0`).
   */
  bossAddSpawnLead: number;
  /** First Endless wave to carry a Kernel boss milestone (§17.4). */
  endlessBossFirst: number;
  /** Endless boss-milestone cadence — waves `endlessBossFirst`, +Every, … */
  endlessBossEvery: number;
  /** Wave at/after which per-kill bounty decay `D(w)` begins (1.0 below it). */
  bountyDecayStart: number;
  /** Per-wave linear decay rate of `D(w)` beyond `bountyDecayStart`. */
  bountyDecayRate: number;
  /** Floor on the per-kill bounty-decay factor `D(w)`. */
  bountyDecayFloor: number;
  /** Max tile distance an Arc chain can jump between links. */
  arcChainRange: number;
  /**
   * Symmetric grid-surplus free band (`supply − demand`) — the Arc Coil's
   * surplus coupling (docs/GAME_DESIGN.md §5/§6) leaves the Arc neutral (×1.0)
   * for any surplus in `[−this, +this]`: a roughly-managed grid (a mild
   * surplus OR a mild deficit) earns neither bonus nor penalty, so basic Pylon
   * spending does not charge the Arc and a slightly under-built grid does not
   * sap it — the re-role does not shift the §8 starter balance. Only a
   * deliberate over-build / a real brownout *past* the band moves the Arc.
   */
  arcSurplusFreeband: number;
  /**
   * Arc Coil damage-multiplier gained per point of grid surplus *past* the
   * free band — and lost per point of brownout (negative surplus). The Arc's
   * per-shot damage is `clamp(1 + eff × this, arcBrownoutDamageFloor,
   * 1 + arcSurplusDamageCap)` where `eff` is the free-band-adjusted surplus.
   */
  arcSurplusDamagePerPoint: number;
  /** Cap on the Arc's positive surplus damage bonus (max multiplier = 1 + this). */
  arcSurplusDamageCap: number;
  /** Floor on the Arc's damage multiplier in a deep brownout. */
  arcBrownoutDamageFloor: number;
  /** Grid surplus past the free band per +1 bonus Arc chain target. */
  arcSurplusChainPer: number;
  /** Cap on the bonus Arc chain targets earned from a rich grid surplus. */
  arcSurplusChainMax: number;
  /**
   * Sniper Rail line-of-fire half-width, in tiles — see docs/GAME_DESIGN.md §5.
   * The railgun lance hits every enemy whose perpendicular distance to the
   * firing line is within this. Thin enough that a path bend carries enemies
   * out of the line, so the tower wants to be aligned with a long straight run.
   */
  sniperBeamHalfWidth: number;
  /** Highest level a tower can be upgraded to. */
  maxTowerLevel: number;
  // --- Overclock (REC 3, Sprint 26) ----------------------------------------
  /**
   * Per-tower OVERCLOCK draw multiplier — see docs/REC3_DESIGN.md §4. While
   * `tower.overclock === true` the tower's effective `powerDraw` is
   * multiplied by this and rounded; the boost feeds straight into
   * `state.power.demand`, so an Overclock spike pulls the grid toward
   * brownout, weakens Arc surplus, and costs the player. Tuned > the rate
   * multiplier so steady-state Overclock is a net efficiency loss —
   * Overclock is right only as a *burst* lever (cf. `overclockRateMult`).
   */
  overclockDrawMult: number;
  /**
   * Per-tower OVERCLOCK fire-rate multiplier (docs/REC3_DESIGN.md §4). The
   * divisor applied to a tower's post-efficiency cooldown when
   * `tower.overclock === true` — values > 1 fire faster (e.g. 1.6 ⇒ +60%
   * fire-rate). Pure read in `applyCooldown`, no RNG, determinism-safe.
   */
  overclockRateMult: number;
  // --- Pylon aura (REC 3.b, Sprint 39 — docs/REC3_DESIGN.md §2.B) -----------
  /**
   * Pylon aura radius in tiles (REC 3.b — docs/REC3_DESIGN.md §2.B,
   * docs/NEXT_BATCH_PLAN.md §2.6). A weapon tower at pixel distance
   * `≤ pylonAuraRadius × tileSize` of a Pylon receives one stack of the
   * Pylon's fire-rate buff (`pylonAuraRateBuff`), capped at
   * `pylonAuraStackCap` stacks. Pure geometry — distance² check on the
   * integer tile centres; no RNG, determinism-safe. Lifts the Pylon's
   * placement axis from "anywhere with a free tile" to "where do I plant
   * my buff sources?" without breaking Sprint 15's global-supply contract.
   */
  pylonAuraRadius: number;
  /**
   * Per-Pylon fire-rate buff contributed by an in-radius Pylon to a weapon
   * tower (REC 3.b — docs/REC3_DESIGN.md §2.B). Stacks additively up to
   * `pylonAuraStackCap` Pylons; a tower's effective post-efficiency cooldown
   * is divided by `1 + n × pylonAuraRateBuff` where `n` is the capped in-
   * radius Pylon count. `0` cleanly disables the lever (1× divisor). Pure
   * read in `applyCooldown` via the cached `Tower.auraRateMult`.
   */
  pylonAuraRateBuff: number;
  /**
   * Maximum number of Pylons whose aura stacks on a single weapon tower
   * (REC 3.b — docs/REC3_DESIGN.md §2.B). Prevents a Pylon-saturated tile
   * from running away on fire-rate. At the default (`pylonAuraRateBuff` 0.10,
   * `pylonAuraStackCap` 2) the cap is +20% fire-rate per tower.
   */
  pylonAuraStackCap: number;
  /**
   * Streak slow resistance — an incoming Cryo slow `factor` is softened to
   * `1 − (1 − factor) × (1 − streakSlowResist)` against the Streak, mirroring
   * the Kernel boss's `bossSlowResist` branch (docs/GAME_DESIGN.md §17.1 +
   * docs/ENEMY_DESIGN.md §3.2 (a)). The Cryo painter's *brittle* leg still
   * applies fully — Streak has no brittle-resistance branch, by design.
   */
  streakSlowResist: number;
  /**
   * Lone-wolf sprint speed multiplier — when no other living enemy is within
   * `streakSprintRadius × tileSize` of a Streak, its tick's `effectiveSpeed`
   * is multiplied by this (docs/ENEMY_DESIGN.md §3.2 (b)). Pure-deterministic
   * per-tick proximity check; no new RNG.
   */
  streakSprintMult: number;
  /**
   * Lone-wolf sprint isolation radius, in tiles — a Streak is *isolated* (and
   * therefore sprinting) when no other living enemy sits inside this radius.
   * Intentionally matches `arcChainRange` so a sprinting Streak by definition
   * sits outside any Arc Coil's chain hop (§3.4 — the Arc is the anti-group
   * tower, the Streak is the anti-group enemy).
   */
  streakSprintRadius: number;
  /** Number of Bit children a Fork spawns when it dies from damage. */
  forkChildCount: number;
  /** Ticks between a Patch healer's repair pulses. */
  patchHealInterval: number;
  /** HP a Patch restores to each enemy in range per pulse. */
  patchHealAmount: number;
  /** Patch healer repair-aura radius in tiles. */
  patchHealRadius: number;
  /** Floor on any single damage instance (armor cannot reduce below this). */
  minDamage: number;
  /** Max Corrosion stacks on one enemy. */
  corrodeMaxStacks: number;
  /** Armor shredded per Corrosion stack. */
  corrodeArmorPerStack: number;
  /** Ticks a Corrosion status lasts before all stacks drop at once. */
  corrodeDurationTicks: number;
  /** Ticks an EMP stun lasts before it expires (Arc Coil L3 capstone). */
  empDurationTicks: number;
  /** Endless threat budget for the first endless wave (wave 11). */
  endlessBudgetBase: number;
  /** Endless threat budget added per wave beyond the campaign length. */
  endlessBudgetPerWave: number;
  /** Every Nth endless wave (by wave number) is a surge wave. */
  endlessSurgeEvery: number;
  /** Budget multiplier applied on a surge wave. */
  endlessSurgeMult: number;
  /** Score bonus per cleared endless wave: depthBonus × (wave − campaign length). */
  endlessDepthBonus: number;
  /** Endless threat-point cost per enemy type. */
  endlessThreatCost: Record<EnemyType, number>;
  /** Endless type-weight table (relative spawn weights). */
  endlessWeights: Record<EnemyType, number>;
  /**
   * Endless per-wave **modifier rotation** start wave (Sprint 47). The first
   * Endless wave that can carry a `WaveDef.modifier`. Set to
   * `campaignLength + 1` (11) — the modifier window opens immediately after
   * the campaign table ends, and the rotation skips surge / boss-milestone
   * waves so the surge spike and boss fights keep their designed identity.
   * See docs/ENDLESS_DESIGN.md §2.2.
   */
  endlessModifierStartWave: number;
  /** Lifetime of a beam visual effect, in ticks. */
  beamTtlTicks: number;
  /** Lifetime of a kill particle, in ticks. */
  particleTtlTicks: number;
  /** Number of particles spawned per kill. */
  particlesPerKill: number;
  /** Per-tick velocity damping applied to kill particles (0..1). */
  particleFriction: number;
  /** Ticks a transient HUD notice stays on screen. */
  noticeTicks: number;
  /** Ticks an enemy keeps its "just hit" flash after taking damage. */
  hitFlashTicks: number;
  /** Floor on power-grid efficiency when used as a divisor (avoids /0). */
  minPowerEfficiency: number;
  /**
   * §18 juice-layer master switch — render-/audio-only. Default `true`; set
   * `false` for stable-pixel automated runs (analogous to `onboardingEnabled`).
   * Gates floating damage numbers, screen shake, boss hit-stop, the richer
   * impact / death VFX, boss SFX and the procedural music loop — none of which
   * touch the deterministic simulation. See docs/GAME_DESIGN.md §18.
   */
  juiceEnabled: boolean;
  /** Screen-shake peak offset (px) for a small impact — a Mortar detonation (§18). */
  shakeSmall: number;
  /** Screen-shake peak offset (px) for a medium impact — Airstrike / life lost / Power Drain (§18). */
  shakeMedium: number;
  /** Screen-shake peak offset (px) for a big impact — a Kernel phase transition / death (§18). */
  shakeBig: number;
  /** Hit-stop micro-freeze (ms) on a Kernel phase transition (§18). */
  hitStopBossPhaseMs: number;
  /** Hit-stop micro-freeze (ms) on the Kernel's death (§18). */
  hitStopBossDeathMs: number;
  /** Max live floating damage numbers — a render pool, oldest evicted (§18). */
  damageNumberPoolMax: number;
  /** Milliseconds a floating damage number rises ≈22 px and fades (§18). */
  damageNumberRiseMs: number;
  /** Per-difficulty multiplier table. */
  difficulties: Record<Difficulty, DifficultyDef>;
  /** Wave-intensity tiers for the build-phase Overload dial (index = tier 0–4). */
  overloadTiers: OverloadTier[];
  /** The three mid-wave active abilities (Overcharge / Airstrike / EMP Burst). */
  abilities: AbilityDef[];
  enemies: Record<EnemyType, EnemyDef>;
  towers: TowersConfig;
  waves: WaveDef[];
  /**
   * The map registry — order = title-screen chip cycle order (Sprint 21).
   * The first entry is the bootstrap default for a fresh run with no `?map=`
   * URL override. Each `id` must match a key in `MAPS` (`src/game/map.ts`).
   * See docs/GAME_DESIGN.md §3.
   */
  maps: MapRegistryEntry[];
  /** The meta-progression unlock tree — see docs/GAME_DESIGN.md §19.2. */
  unlocks: UnlockNode[];
  /** Flux earned per fully-cleared wave at run end (§19.1). */
  fluxPerWave: number;
  /** Final-score divisor folded into the Flux award (§19.1). */
  fluxScoreDivisor: number;
  /** Bonus Flux for a Campaign/Maze win (§19.1). */
  fluxWinBonus: number;
  /** Per-difficulty Flux multiplier (§19.1). */
  difficultyFluxMult: Record<Difficulty, number>;
  /** Per-mode Flux multiplier (§19.1). */
  modeFluxMult: Record<RunMode, number>;
  /** Max runs kept in the Scoreboard run history — oldest evicted (§20.1). */
  runHistoryMax: number;
  /** localStorage key for the single meta-progression profile blob (§19.5). */
  profileKey: string;
  /** localStorage key base for the Campaign high score (a `.<difficulty>` suffix is appended). */
  highScoreKey: string;
  /** localStorage key base for the Endless high score (a `.<difficulty>` suffix is appended). */
  endlessHighScoreKey: string;
  /** localStorage key base for the Endless deepest wave (a `.<difficulty>` suffix is appended). */
  endlessBestWaveKey: string;
  /** localStorage key base for the Maze high score (a `.<difficulty>` suffix is appended). */
  mazeHighScoreKey: string;
  /** localStorage key base for the Maze deepest wave (a `.<difficulty>` suffix is appended). */
  mazeBestWaveKey: string;
  colors: {
    background: string;
    grid: string;
    pathFill: string;
    pathGlow: string;
    spawn: string;
    core: string;
    hudBg: string;
    hudText: string;
    hudMuted: string;
    accent: string;
    warning: string;
    rangeRing: string;
    good: string;
    bad: string;
    cryo: string;
    frost: string;
    mortar: string;
    sniper: string;
    corrode: string;
    emp: string;
    patch: string;
    kernel: string;
  };
}

export const CONFIG: GameConfig = {
  canvas: { width: 800, height: 596 },

  loop: {
    updateHz: 60,
    maxStepsPerFrame: 5,
    maxFrameDelta: 0.25,
  },

  hudHeight: 116,
  grid: { cols: 20, rows: 12, tileSize: 40 },

  tickRate: 10,
  fastForward: 2,
  campaignLength: 10,
  seed: 0xc0ffee,
  difficulty: 'normal',
  onboardingEnabled: true,

  // Sprint 19 §8 retune: Normal base lives 15 → 18. A mediocre starter board
  // was being timed out by the wave 9–10 leak budget (the 10-wave Campaign
  // packs ~24 lives of leak potential into the finale escort + Kernel alone),
  // even with the wave-9 / wave-10 trims. The base bump is *Normal-targeted*:
  // Easy and Hard multipliers are dropped in lockstep so their effective
  // starting lives are unchanged (Easy 21, Hard 11, Normal now 18). Lifts the
  // §8 Normal mediocre band toward 40–55% without raising Easy / Hard.
  startingGold: 120,
  startingLives: 18,
  sellRefundRate: 0.7,
  // Sprint 19 §8 retune: wave-clear bonus stepped up 15+4w → 20+6w. The
  // wave-clear bonus is the *un-decayed* recovery lever (docs/GAME_DESIGN.md
  // §9) — a player who keeps clearing waves keeps a stable income even as the
  // per-kill decay floors out. Mediocre boards lean on this exit-ramp far
  // more than competent ones (which run a per-kill surplus and rarely need
  // the clear bonus to afford the next upgrade), so dialing the slope up
  // disproportionately lifts the §8 mediocre band toward 40–55% without
  // raising the competent ceiling much. Late waves see ≈+18 gold/clear at
  // wave 9 (was 51, now 74) and +25 at wave 10 (was 55, now 80).
  waveBonusBase: 20,
  waveBonusPerWave: 6,
  waveClearScore: 250,
  livesScoreBonus: 100,
  winFlourishMultiplier: 1.5,

  // Maze Mode wave-clear length bonus — see docs/GAME_DESIGN.md §16.7. A longer
  // route trades raw tower efficiency (dead wall) for reward, so winding is a
  // genuine choice: primarily score, plus a modest gold trickle, both scaling
  // per route segment past the ~19-segment gauntlet baseline.
  mazeBaselineLength: 19,
  mazeBonusScorePerTile: 8,
  mazeBonusGoldPerTile: 1,
  // +50 gold on a Maze run — ≈5 Walls, enough to carve an opening detour
  // *and* still arm it. Modest by design: the maze pays for itself from there
  // via the §16.7 length bonus and wave-clear income. See §16.9.
  mazeStartGoldBonus: 50,

  reactorBasePower: 20,
  hpGrowthLinear: 0.11,
  hpGrowthQuad: 0.013,
  lateSpeedRampStart: 8,
  lateSpeedRampPerWave: 0.03,
  lateSpeedRampMax: 1.6,

  // The Kernel boss — see docs/GAME_DESIGN.md §17. A multi-phase capstone
  // enemy: a huge HP pool that rides the §8 compounding curve, three HP-gated
  // phases that accelerate it and spawn adds, and a telegraphed Power Drain
  // attack on the §6 power grid. `bossBaseHp` 1300→950 for the wave-10 move:
  // a 10-wave board reaching the boss is smaller / less upgraded (§17.4).
  bossBaseHp: 950,
  bossHpWaveMultCap: 5.088, // ≈ hpMultiplier(15) — caps the boss wave-curve (§17.4)
  bossBounty: 500,
  bossLifeDmg: 10,
  bossArmor: 0,
  bossSlowResist: 0.5,
  // Sprint 42 Kernel rework — `docs/KERNEL_REWORK_DESIGN.md` §3.2 Changes 1+2+3+4.
  // Phase-2 band widens 35pp → 43pp (Change 1: a strong build no longer
  // compresses Fork-Bomb to 3-5s — the spec-intended 8-12s add-management
  // window is back). Phase-3 band stays the 22pp final-sprint dash.
  bossPhase2HpFrac: 0.65,
  bossPhase3HpFrac: 0.22,
  bossPhaseSpeed: [0.32, 0.42, 0.58],
  // Drain cadence loosens in phases 2/3 (Change 4): more spacing between
  // drains keeps the §15 Overcharge cooldown (600 ticks) in a real dueling
  // window with each drain telegraph rather than face-planting an Overclocked
  // grid into a 0.25× efficiency hole. 80 still beats the 60-tick Overcharge
  // window in phase 3 — bad cadence still costs the player one drain/phase.
  bossDrainInterval: [130, 100, 80],
  bossDrainTelegraph: 25,
  bossDrainDuration: 60,
  bossDrainMult: 0.5,
  // Phase-2 adds drop 3→2 Packets (Change 3): the Sniper-lance co-line gift
  // capped one-shotted a 3-Packet pod; 2 Packets keeps the distraction role
  // without the silent trivialise. Total threat budget unchanged together
  // with Change 1's wider band — the pod gets more walking time too.
  bossPhase2Adds: { packet: 2 },
  // Phase-3 adds rotate Streak in (Change 2): the post-S31 sprinting add
  // belongs *inside* the boss fight, not just on the wave-10 escort. The
  // Streak spawns co-located with the pod for ~10 ticks (below
  // `streakSprintRadius`), then peels off and sprints — the "Streak runs
  // ahead of its escort" pattern, delivered as a boss-add problem. One
  // Fork dropped to keep the spawn count at 5.
  bossPhase3Adds: { fork: 1, bit: 3, streak: 1 },
  // §3.2 Change 5: phase-entry pods spawn one tile ahead of the boss along
  // the shared route — see `bossAddSpawnLead` JSDoc on `GameConfig`.
  bossAddSpawnLead: 1,
  // Re-anchored 25→20 with the 10-wave recompression (still campaignLength+10).
  endlessBossFirst: 20,
  endlessBossEvery: 10,

  bountyDecayStart: 6,
  // 0.0444→0.09: the decay still completes its arc inside the 10-wave campaign
  // (D reaches the 0.64 floor at the wave-10 finale). A linear curve element,
  // safe to recompress — unlike the quadratic HP curve. See §9.
  bountyDecayRate: 0.09,
  bountyDecayFloor: 0.64,
  arcChainRange: 1.5,
  // Arc Coil power-surplus coupling — docs/GAME_DESIGN.md §5/§6. A surplus
  // inside the symmetric ±12 free band (a roughly-managed grid) earns no
  // bonus and no penalty; past +12 the damage multiplier ramps 0.025/point to
  // a +0.8 cap (×1.8) and the chain gains +1 target per 16 points to a +2
  // cap; below −12 (a real brownout) damage ramps down to a ×0.5 floor. Pure
  // deterministic on the integer surplus — validated by s15.mjs.
  arcSurplusFreeband: 12,
  arcSurplusDamagePerPoint: 0.025,
  arcSurplusDamageCap: 0.8,
  arcBrownoutDamageFloor: 0.5,
  arcSurplusChainPer: 16,
  arcSurplusChainMax: 2,
  sniperBeamHalfWidth: 0.55,
  maxTowerLevel: 3,
  // --- Overclock (REC 3, Sprint 26) — see docs/REC3_DESIGN.md §4.4 -----------
  // +75% draw, +60% rate → ~1.6× DPS for ~1.75× draw, net efficiency ~0.91.
  // Steady-state Overclock is a loss; *bursty* Overclock at a crescendo is a
  // win. The asymmetric values are the design knob.
  overclockDrawMult: 1.75,
  overclockRateMult: 1.6,
  // --- Pylon aura (REC 3.b, Sprint 39 — docs/REC3_DESIGN.md §2.B) -----------
  // A small fire-rate buff a Pylon emits to weapon towers within `pylonAura
  // Radius` tiles, stacking up to `pylonAuraStackCap` Pylons. Radius matches
  // `arcChainRange` so the aura touches the same "right-next-door"
  // neighbourhood the Arc Coil's chain already lives in — a single visual /
  // mental class for "what counts as adjacent." See REC3_DESIGN.md §2.B and
  // NEXT_BATCH_PLAN.md §2.6 for the keystone fit.
  //
  // Sprint 46 §8 re-retune (PLAYTEST_3.md §2 / docs/GAME_DESIGN.md §8.S46):
  // `pylonAuraRateBuff` dropped from 0.10 → 0.01. The S39 launch value
  // (+10% per Pylon, cap 2 → +20% saturated) silently lifted the harness
  // mediocre AI's Pulse-next-to-Pylon cluster onto the +20% rate cap and
  // pushed Normal/mediocre from on-band 44% → 72% (96-seed s46.mjs reading
  // against contract 1.18.0, post-`a2fa5d0` boss tuning). The s46 sweep
  // (buff ∈ {0.00, 0.01, 0.02, 0.03, 0.04, 0.05, 0.07, 0.10}) showed a
  // near-linear ~3 pp / 0.01 buff slope on Normal/mediocre; only buff ≤ 0.01
  // lands the cell back inside the 40–55% band. 0.01 (not 0.00) keeps the
  // lever *mechanically* present — `auraRateMult` is still > 1 on saturated
  // placements (1.01× at n=1, 1.02× at n=2) — so the Sprint-39 contract
  // (pylonAura.spec.ts asserts `≈ 1 + n × buff` against the live `buff`
  // constant) holds bit-for-bit, and the placement axis still reads as "more
  // Pylons → more uplift", just smaller. Radius (1.5) and cap (2) are kept
  // — the s46 sweep confirmed cap1 alone barely moved Normal/mediocre (the
  // harness AI's geometry rarely saturates n=2 in practice), and a radius
  // cut to 1.0 only trimmed the cell to 69%. The re-design of the lever
  // (per PLAYTEST_3.md §6.3 P1) is queued for the next batch; this commit
  // restores the §8 matrix to 6/8 on-band as the one-knob emergency tune.
  pylonAuraRadius: 1.5,
  pylonAuraRateBuff: 0.01,
  pylonAuraStackCap: 2,
  // Streak tunables — docs/ENEMY_DESIGN.md §3.7. Slow-resistance 0.6 makes the
  // L3 Cryo's 0.30 slow factor settle at 0.72 against a Streak (the design
  // anchor); sprint mult 1.5 lifts an isolated Streak from 1.6 → 2.4 t/s (a
  // *faster than Packet* sprint); sprint radius 1.5 matches `arcChainRange`
  // so a sprinting Streak is — by definition — outside any Arc chain hop.
  streakSlowResist: 0.6,
  streakSprintMult: 1.5,
  streakSprintRadius: 1.5,
  forkChildCount: 2,
  patchHealInterval: 10,
  patchHealAmount: 4,
  patchHealRadius: 2.0,

  minDamage: 1,
  corrodeMaxStacks: 5,
  corrodeArmorPerStack: 2,
  corrodeDurationTicks: 50,
  empDurationTicks: 5,

  endlessBudgetBase: 40,
  endlessBudgetPerWave: 14,
  endlessSurgeEvery: 5,
  endlessSurgeMult: 1.5,
  endlessDepthBonus: 25,
  endlessThreatCost: { bit: 1, packet: 1, fork: 3, daemon: 5, cipher: 4, patch: 4, buffer: 4, streak: 3 },
  endlessWeights: { bit: 24, packet: 22, fork: 15, daemon: 11, cipher: 11, patch: 9, buffer: 8, streak: 9 },
  // Sprint 47: per-wave modifier rotation starts at wave 11 (just past the
  // campaign table). The modifier system itself lives in `src/game/endless.ts`.
  endlessModifierStartWave: 11,

  beamTtlTicks: 2,
  particleTtlTicks: 6,
  particlesPerKill: 7,
  particleFriction: 0.86,
  noticeTicks: 25,
  hitFlashTicks: 2,
  minPowerEfficiency: 0.05,

  // §18 juice layer — render-/audio-only feedback tunables (game-feel pass).
  // The whole layer is gated by `juiceEnabled` and driven by a dedicated
  // render-only RNG, so it never shifts a seed or a QA golden value.
  juiceEnabled: true,
  shakeSmall: 2,
  shakeMedium: 5,
  shakeBig: 9,
  hitStopBossPhaseMs: 60,
  hitStopBossDeathMs: 110,
  damageNumberPoolMax: 64,
  damageNumberRiseMs: 600,

  // Per-difficulty multipliers over the Normal base — see docs/GAME_DESIGN.md §8.
  difficulties: {
    easy: {
      // Sprint 19 §8 retune: `startingLives` base bumped 15 → 18 to lift the
      // Normal mediocre band; Easy's lives mult drops 1.4 → 1.17 so the Easy
      // starting lives stay ≈ 21 (was 21). The other Easy multipliers are
      // unchanged — the §8 Easy bands are already on target.
      enemyHpMult: 0.64,
      enemyCountMult: 0.85,
      enemySpeedMult: 0.9,
      startingGoldMult: 1.35,
      startingLivesMult: 1.17,
      bountyMult: 1.15,
    },
    normal: {
      enemyHpMult: 1.0,
      enemyCountMult: 1.0,
      enemySpeedMult: 1.0,
      startingGoldMult: 1.0,
      startingLivesMult: 1.0,
      bountyMult: 1.0,
    },
    hard: {
      // Sprint 19 §8 retune: Hard sat at 70% competent win rate (target band
      // is 25–40%) — the post-keystone competent build still steamrolls Hard
      // because the only debuffs were +10% speed, −15% lives, −10% gold.
      // The spec keeps Hard's `enemyHpMult` at 1.0 ("its bite is a leaner
      // economy, not toughened individuals" — docs §8), so the retune tightens
      // the *economy* knobs and widens the speed bump 1.10 → 1.15 so the
      // existing late-wave ramp bites harder.
      enemyHpMult: 1.0,
      enemyCountMult: 1.0,
      enemySpeedMult: 1.15,
      // Sprint 36 §8 retune (E1): startingGoldMult 0.92 → 0.97. The
      // ENDGAME §5 package called for "1.00 → 1.05" against a stale Sprint-9
      // baseline; the live post-Sprint-19 value was 0.92, so the equivalent
      // +5 pp nudge resolves to 0.97. Eases Hard's L3-economy slightly so
      // the marginal "I can almost afford the L3 Cryo" cell stops bleeding
      // out at wave 8; validated post-Streak by tools/balance/s36.mjs.
      // An earlier sweep at 0.85/0.75/0.61 over-tightened — Hard/competent
      // collapsed to ~8% with the harness's relaxed L3-cascade competent
      // AI. The economy is still leaner than pre-retune (0.97/0.75/0.67 vs
      // the original 1.00/0.90/0.85) and the speed bump bites; with the
      // keystone-roled towers a competent build still lands inside the §8
      // 25–40% band on Hard.
      startingGoldMult: 0.97,
      startingLivesMult: 0.67,
      bountyMult: 0.85,
    },
  },

  // Build-phase Overload dial — see docs/GAME_DESIGN.md §8. Index = tier 0–4;
  // tier 0 is balance-neutral. `goldMult` stays below the wave's combined
  // hp×count threat so the dial is monotonic (higher tier = genuinely harder);
  // `scoreMult` climbs faster so the leaderboard still rewards brave play.
  overloadTiers: [
    { name: 'Nominal', hpMult: 1.0, speedMult: 1.0, countMult: 1.0, goldMult: 1.0, scoreMult: 1.0 },
    { name: 'Charged', hpMult: 1.3, speedMult: 1.04, countMult: 1.15, goldMult: 1.1, scoreMult: 1.5 },
    { name: 'Surging', hpMult: 1.65, speedMult: 1.08, countMult: 1.3, goldMult: 1.2, scoreMult: 2.1 },
    { name: 'Critical', hpMult: 2.05, speedMult: 1.12, countMult: 1.45, goldMult: 1.35, scoreMult: 2.8 },
    { name: 'Meltdown', hpMult: 2.5, speedMult: 1.16, countMult: 1.6, goldMult: 1.5, scoreMult: 3.6 },
  ],

  // Mid-wave active abilities — see docs/GAME_DESIGN.md §15. Sprint 20 re-role
  // (REC 6): each ability now has a *proactive* optimal use the player plans
  // around.
  //
  //  - Overcharge: 60-tick BURST window at eff 1.6 followed by a 40-tick
  //    AFTERSHOCK brownout at eff 0.55. The burst is the spike; the aftershock
  //    is the cost. Plan the spike for a known peak wave moment.
  //  - Airstrike: smaller initial blast (28 → 18) + a 120-tick (12 s) lingering
  //    SCORCH ZONE that chip-damages and brittle-marks (×1.30) every enemy
  //    inside it. A *placement* decision — drop on a predicted chokepoint.
  //  - EMP Burst: 20-tick stun + a field-wide brittle window (×1.20) + a
  //    per-stunned-enemy SCORE BONUS scaling with the wave's Overload tier.
  //    The cash-out moment of a high-Overload plan.
  //
  // The previous "panic button" effects (the fire-rate burst, the nuke, the
  // stun) are still present — they are just no longer the whole ability.
  abilities: [
    {
      id: 'overcharge',
      name: 'Overcharge',
      cost: 50,
      cooldownTicks: 280,
      durationTicks: 60,
      efficiency: 1.6,
      aftershockTicks: 40,
      aftershockEfficiency: 0.55,
      radius: 0,
      damage: 0,
      scorchTicks: 0,
      scorchTickDamage: 0,
      scorchBrittleAmp: 1.0,
      scorchBrittleTicks: 0,
      empTicks: 0,
      empBrittleAmp: 1.0,
      empScoreBonusPer: 0,
    },
    {
      id: 'airstrike',
      name: 'Airstrike',
      cost: 45,
      cooldownTicks: 200,
      durationTicks: 0,
      efficiency: 0,
      aftershockTicks: 0,
      aftershockEfficiency: 0,
      radius: 2.0,
      damage: 18,
      scorchTicks: 120,
      scorchTickDamage: 2,
      scorchBrittleAmp: 1.30,
      scorchBrittleTicks: 8,
      empTicks: 0,
      empBrittleAmp: 1.0,
      empScoreBonusPer: 0,
    },
    {
      id: 'empBurst',
      name: 'EMP Burst',
      cost: 80,
      cooldownTicks: 360,
      durationTicks: 0,
      efficiency: 0,
      aftershockTicks: 0,
      aftershockEfficiency: 0,
      radius: 0,
      damage: 0,
      scorchTicks: 0,
      scorchTickDamage: 0,
      scorchBrittleAmp: 1.0,
      scorchBrittleTicks: 0,
      empTicks: 20,
      empBrittleAmp: 1.20,
      empScoreBonusPer: 8,
    },
  ],

  enemies: {
    bit: { label: 'Bit', hp: 12, speed: 1.0, bounty: 3, lifeDmg: 1, color: '#6cf2a0', radius: 8, baseArmor: 0, baseShield: 0, shieldRegenDelay: 0, shieldRegenRate: 0 },
    packet: { label: 'Packet', hp: 7, speed: 2.0, bounty: 4, lifeDmg: 1, color: '#ffd166', radius: 7, baseArmor: 0, baseShield: 0, shieldRegenDelay: 0, shieldRegenRate: 0 },
    daemon: { label: 'Daemon', hp: 69, speed: 0.6, bounty: 10, lifeDmg: 3, color: '#ff4d6d', radius: 14, baseArmor: 0, baseShield: 0, shieldRegenDelay: 0, shieldRegenRate: 0 },
    fork: { label: 'Fork', hp: 32, speed: 0.9, bounty: 8, lifeDmg: 2, color: '#c77dff', radius: 10, baseArmor: 0, baseShield: 0, shieldRegenDelay: 0, shieldRegenRate: 0 },
    cipher: { label: 'Cipher', hp: 52, speed: 0.75, bounty: 12, lifeDmg: 2, color: '#5b8cff', radius: 11, baseArmor: 4, baseShield: 0, shieldRegenDelay: 0, shieldRegenRate: 0 },
    patch: { label: 'Patch', hp: 40, speed: 0.85, bounty: 13, lifeDmg: 1, color: '#27d3a8', radius: 10, baseArmor: 0, baseShield: 0, shieldRegenDelay: 0, shieldRegenRate: 0 },
    buffer: { label: 'Buffer', hp: 28, speed: 0.8, bounty: 14, lifeDmg: 2, color: '#4cc9f0', radius: 11, baseArmor: 0, baseShield: 45, shieldRegenDelay: 30, shieldRegenRate: 3 },
    // Streak — the Sprint 31 lane-runner (docs/ENEMY_DESIGN.md §3.1). A relentless
    // magenta courier: base 1.6 t/s (faster than every other ground unit but the
    // Packet), HP 22 (medium-low — a single L2 Sniper kills it, an L1 Pulse +
    // L1 Sniper does too), no armour, no shield. Bounty 9 sits between the Fork
    // (8) and the Daemon (10); lifeDmg 2 makes leaks expensive on the 18-life
    // Campaign. Its two non-trivial behaviours — slow-resistance and lone-wolf
    // sprint — live in `src/game/systems.ts` (`applySlow` + `moveEnemies`),
    // gated by the `streakSlowResist` / `streakSprintMult` / `streakSprintRadius`
    // tunables below.
    streak: { label: 'Streak', hp: 22, speed: 1.6, bounty: 9, lifeDmg: 2, color: '#ff61c5', radius: 7, baseArmor: 0, baseShield: 0, shieldRegenDelay: 0, shieldRegenRate: 0 },
  },

  // Tower upgrade tables — see docs/GAME_DESIGN.md §5. Balancing needs no code.
  towers: {
    pulse: {
      label: 'Pulse Turret',
      color: '#2de2e6',
      weapon: true,
      cost: 50,
      levels: [
        { damage: 4, range: 2.5, cooldownTicks: 5, powerDraw: 4 },
        { damage: 7, range: 2.8, cooldownTicks: 4, powerDraw: 6 },
        { damage: 12, range: 3.2, cooldownTicks: 3, powerDraw: 9 },
      ],
      upgradeCosts: [45, 80],
    },
    arc: {
      label: 'Arc Coil',
      color: '#b967ff',
      weapon: true,
      cost: 80,
      levels: [
        { damage: 3, range: 2.0, cooldownTicks: 10, powerDraw: 7, chainTargets: 3 },
        { damage: 5, range: 2.3, cooldownTicks: 8, powerDraw: 11, chainTargets: 4 },
        { damage: 8, range: 2.6, cooldownTicks: 7, powerDraw: 16, chainTargets: 5 },
      ],
      upgradeCosts: [70, 120],
    },
    cryo: {
      label: 'Cryo Emitter',
      color: '#7ad7ff',
      weapon: true,
      // Tight-radius CHOKEPOINT PAINTER (docs/GAME_DESIGN.md §5). A small slow
      // zone (range 1.2 / 1.4 / 1.6 by level; AoE cap 3 / 4 / 5) paints only
      // the tile-plus-neighbours patch at the emitter — a placement decision
      // ("which pinch is worth painting?"), NOT the pre-re-role board-wide ~2×
      // multiplier (STRATEGY_ANALYSIS.md §2.3 / §4 R2). Painted enemies gain a
      // stronger local slow (0.50 / 0.40 / 0.30 — was 0.55 / 0.45 / 0.35) AND
      // a NEW brittle mark — every other tower's damage gets a +20% / +30% /
      // +40% uplift against them. Both statuses REFRESH on reapply (slow `min`,
      // brittle `max`); overlapping Cryos cannot rebuild the board-wide
      // multiplier by stacking. Damage stays 1 — Cryo never directly kills.
      // `brittleDuration` is tuned slightly longer than `slowDuration` so an
      // enemy painted at the front edge keeps its vulnerability for the next
      // damage tick after the slow expires. Validated by tools/balance/s17.mjs.
      cost: 65,
      levels: [
        { damage: 1, range: 1.4, cooldownTicks: 8, powerDraw: 6, slowFactor: 0.50, slowDuration: 22, aoeTargets: 3, brittleAmp: 1.20, brittleDuration: 22 },
        { damage: 1, range: 1.4, cooldownTicks: 7, powerDraw: 9, slowFactor: 0.40, slowDuration: 26, aoeTargets: 4, brittleAmp: 1.30, brittleDuration: 26 },
        { damage: 1, range: 1.6, cooldownTicks: 6, powerDraw: 13, slowFactor: 0.30, slowDuration: 30, aoeTargets: 5, brittleAmp: 1.40, brittleDuration: 30 },
      ],
      upgradeCosts: [60, 95],
    },
    mortar: {
      label: 'Splash Mortar',
      color: '#ff5630',
      weapon: true,
      cost: 90,
      // Minimum-range indirect-fire emplacement (docs/GAME_DESIGN.md §5). A
      // shot is lobbed in a steep arc, so the Mortar cannot hit anything inside
      // `minRange` — its target selection skips the dead zone and fires only at
      // targets in the annulus between `minRange` and `range`. `minRange` is a
      // constant 2.0-tile dead zone (the tower's signature); each upgrade
      // extends the outer `range`, widening the firing annulus. The tower wants
      // a deep-back tile — hugging the path/corner drops the route into the
      // dead zone — so it breaks OVERVOLT's universal "corner is best" rule.
      // `range` is tuned so the annulus AREA (π·(range²−minRange²)) ≈ the
      // pre-re-role disc area at each level: the re-role is a true sidegrade
      // (a new role, NOT a coverage buff — balance harness s14.mjs).
      levels: [
        { damage: 7, range: 3.6, minRange: 2.0, cooldownTicks: 14, powerDraw: 9, blastRadius: 1.2 },
        { damage: 11, range: 3.9, minRange: 2.0, cooldownTicks: 12, powerDraw: 13, blastRadius: 1.5 },
        { damage: 17, range: 4.1, minRange: 2.0, cooldownTicks: 10, powerDraw: 18, blastRadius: 1.8 },
      ],
      upgradeCosts: [85, 145],
    },
    sniper: {
      label: 'Sniper Rail',
      color: '#ff2e88',
      weapon: true,
      cost: 110,
      // Line-of-fire pierce lance (docs/GAME_DESIGN.md §5): a shot fires down a
      // straight line through the primary target and hits EVERY enemy on that
      // line within `sniperBeamHalfWidth`. Armor-piercing — the lance punches
      // through armor. Per-shot damage is moderate (it is a line tower now, not
      // a single-target nuke): its payoff is multi-hit when the tower is
      // aligned with a long straight path run, and weak on a corner where the
      // path bends out of the line. Still defaults to `strongest` — the line
      // then anchors on the toughest in-range enemy (an elite, or the boss).
      armorPiercing: true,
      defaultTargetMode: 'strongest',
      levels: [
        { damage: 13, range: 5.0, cooldownTicks: 18, powerDraw: 8 },
        { damage: 22, range: 5.5, cooldownTicks: 15, powerDraw: 12 },
        { damage: 34, range: 6.0, cooldownTicks: 12, powerDraw: 17 },
      ],
      upgradeCosts: [100, 165],
    },
    pylon: {
      label: 'Pylon',
      color: '#f7d51d',
      weapon: false,
      cost: 40,
      levels: [
        { damage: 0, range: 0, cooldownTicks: 0, powerDraw: 0, supply: 10 },
        { damage: 0, range: 0, cooldownTicks: 0, powerDraw: 0, supply: 22 },
        { damage: 0, range: 0, cooldownTicks: 0, powerDraw: 0, supply: 40 },
      ],
      upgradeCosts: [50, 90],
    },
    // Wall — the dedicated Maze-Mode blocker (docs/GAME_DESIGN.md §16.9). Cheap
    // (10 gold), draws / supplies no power, has no gun, no targeting and no
    // upgrades — it exists only to occupy and block its tile so the player can
    // shape the route without spending a whole tower on every dead wall tile.
    // It rides the shared placement / sell / blocked-tile pipeline (so the
    // §16.4 no-wall-off rule and the 70% sell refund apply for free); the three
    // identical levels and zeroed `upgradeCosts` are inert (`upgradable: false`).
    wall: {
      label: 'Wall Node',
      color: '#5d6f99',
      weapon: false,
      cost: 10,
      upgradable: false,
      levels: [
        { damage: 0, range: 0, cooldownTicks: 0, powerDraw: 0 },
        { damage: 0, range: 0, cooldownTicks: 0, powerDraw: 0 },
        { damage: 0, range: 0, cooldownTicks: 0, powerDraw: 0 },
      ],
      upgradeCosts: [0, 0],
    },
  },

  // 10-wave Campaign director table (Normal counts) — see docs/GAME_DESIGN.md
  // §8. Sprint 9 recompressed the run from 15 waves to 10: same arc, faster
  // cadence — one new enemy type per wave 2–8, a full-roster crescendo at 9,
  // and the Kernel boss as the wave-10 finale (§17.4).
  waves: [
    // Wave 1 — Bit.
    { groups: [{ type: 'bit', count: 10, interval: 11 }] },
    // Wave 2 — Packet debuts.
    {
      groups: [
        { type: 'bit', count: 8, interval: 9 },
        { type: 'packet', count: 8, interval: 8 },
      ],
    },
    // Wave 3.
    {
      groups: [
        { type: 'packet', count: 16, interval: 6 },
        { type: 'bit', count: 10, interval: 6 },
      ],
    },
    // Wave 4 — Daemon debuts.
    {
      groups: [
        { type: 'bit', count: 12, interval: 6 },
        { type: 'daemon', count: 4, interval: 22 },
      ],
    },
    // Wave 5 — Fork debuts.
    {
      groups: [
        { type: 'packet', count: 18, interval: 5 },
        { type: 'fork', count: 5, interval: 16 },
        { type: 'daemon', count: 3, interval: 20 },
      ],
    },
    // Wave 6 — Patch debuts.
    {
      groups: [
        { type: 'bit', count: 20, interval: 4 },
        { type: 'fork', count: 6, interval: 14 },
        { type: 'patch', count: 2, interval: 26 },
        { type: 'daemon', count: 5, interval: 16 },
      ],
    },
    // Wave 7 — Cipher debuts.
    {
      groups: [
        { type: 'packet', count: 26, interval: 4 },
        { type: 'fork', count: 7, interval: 13 },
        { type: 'patch', count: 3, interval: 22 },
        { type: 'cipher', count: 4, interval: 20 },
        { type: 'daemon', count: 6, interval: 15 },
      ],
    },
    // Wave 8 — Buffer debuts; the full seven-type roster is out.
    // Sprint 36 §8 retune (E3): bit 28 → 24, cipher 6 → 5 — the buffer-debut
    // swarm was the historical mediocre / Hard/expert cliff (ENDGAME §2.2
    // loss-wave histogram). The painter's tighter zone (post-keystone) can
    // not absorb 28 bits @ interval 3 plus 6 ciphers; trimming both groups
    // by one rotation (~10 % wave-8 effective HP) gives the first-encounter
    // mediocre player a survivable Buffer wave. Buffer / fork / patch /
    // daemon counts are unchanged — the Sprint 19 retune already trimmed
    // them, and the role-rehearsal arc is preserved.
    {
      groups: [
        { type: 'bit', count: 24, interval: 3 },
        { type: 'fork', count: 9, interval: 11 },
        { type: 'patch', count: 3, interval: 20 },
        { type: 'cipher', count: 5, interval: 17 },
        { type: 'buffer', count: 3, interval: 24 },
        { type: 'daemon', count: 8, interval: 14 },
      ],
    },
    // Wave 9 — full-roster crescendo + Streak debut (Sprint 31). The Streak
    // salvo (4 on Normal — per-difficulty count scaling via the existing
    // `enemyCountMult`, so Easy ≈ 3 and Hard 4) is inserted *between* the
    // daemon push and the closing Bits, so the new enemy arrives mid-crescendo
    // and the cluster has to deal with it concurrently with the late Daemons
    // (docs/ENEMY_DESIGN.md §4.1). Sprint 19 §8 retune counts (packet/fork/
    // patch/cipher/buffer/daemon/bit) are unchanged.
    {
      groups: [
        { type: 'packet', count: 34, interval: 3 },
        { type: 'fork', count: 10, interval: 9 },
        { type: 'patch', count: 3, interval: 18 },
        { type: 'cipher', count: 7, interval: 14 },
        { type: 'buffer', count: 3, interval: 20 },
        { type: 'daemon', count: 9, interval: 12 },
        { type: 'streak', count: 4, interval: 8 },
        { type: 'bit', count: 20, interval: 3 },
      ],
    },
    // Wave 10 — the campaign finale: a trimmed seven-type escort capped by the
    // Kernel boss as the wave's final group (docs/GAME_DESIGN.md §17.4).
    // Sprint 31 (docs/ENEMY_DESIGN.md §4.1): a 2-Streak salvo rides into the
    // escort *before* the Kernel, so the player is fighting the Streak and
    // bracing for the boss simultaneously — the wave-9 introduction's test.
    // Sprint 19 §8 retune economy (daemon 6 / cipher 5 / buffer 3 / fork 6)
    // is unchanged.
    {
      groups: [
        { type: 'daemon', count: 6, interval: 14 },
        { type: 'patch', count: 2, interval: 16 },
        { type: 'cipher', count: 5, interval: 14 },
        { type: 'buffer', count: 3, interval: 18 },
        { type: 'fork', count: 6, interval: 10 },
        { type: 'packet', count: 22, interval: 3 },
        { type: 'streak', count: 2, interval: 14 },
        { type: 'bit', count: 14, interval: 3 },
        { type: 'kernel', count: 1, interval: 1 },
      ],
    },
  ],

  // Map registry — see docs/GAME_DESIGN.md §3. The title-screen Map chip cycles
  // through this list in order; the first entry is the bootstrap default for a
  // fresh run with no `?map=<id>` URL override. Each `id` must match a key in
  // `MAPS` (`src/game/map.ts`), which carries the actual path / blocked-tile
  // data. Sprint 21 added `cramped` — the first constrained-build-space map.
  // Sprint 37 added `lines` — the long-straights axis follow-on (REC 4,
  // docs/STRATEGY_ANALYSIS.md §6 property 2). Sprint 43 added `serpentine` —
  // the tight-serpentine axis (REC 4, STRATEGY_ANALYSIS §6 property 4;
  // PLAYTEST_KEYSTONE §4 third map type).
  maps: [
    { id: 'default', label: 'Datawash', tagline: 'Open S-bend — the classic OVERVOLT layout.' },
    {
      id: 'cramped',
      label: 'Switchyard',
      tagline: 'Cramped lanes — every tile is contested.',
    },
    {
      id: 'lines',
      label: 'Long Lines',
      tagline: 'Long straights — lane the lance.',
    },
    {
      id: 'serpentine',
      label: 'Snake Run',
      tagline: 'Tight serpentine — Cryo painter shines.',
    },
  ],

  // The meta-progression unlock tree — see docs/GAME_DESIGN.md §19.2. Eleven
  // nodes across four branches; the starter loadout (Pulse / Arc / Cryo /
  // Pylon, Overcharge, Overload 0–2, Campaign, Easy/Normal) is NOT in the tree
  // — it is always available. Total ≈ 675 Flux.
  unlocks: [
    { id: 'tower.mortar', name: 'Splash Mortar', branch: 'arsenal', cost: 50, grants: { kind: 'tower', tower: 'mortar' } },
    { id: 'tower.sniper', name: 'Sniper Rail', branch: 'arsenal', cost: 90, prereq: 'tower.mortar', grants: { kind: 'tower', tower: 'sniper' } },
    { id: 'ability.airstrike', name: 'Airstrike', branch: 'arsenal', cost: 60, grants: { kind: 'ability', ability: 'airstrike' } },
    { id: 'ability.emp', name: 'EMP Burst', branch: 'arsenal', cost: 80, prereq: 'ability.airstrike', grants: { kind: 'ability', ability: 'empBurst' } },
    { id: 'mode.endless', name: 'Endless Mode', branch: 'modes', cost: 45, grants: { kind: 'mode', mode: 'endless' } },
    { id: 'mode.maze', name: 'Maze Mode', branch: 'modes', cost: 70, prereq: 'mode.endless', grants: { kind: 'mode', mode: 'maze' } },
    { id: 'difficulty.hard', name: 'Hard Difficulty', branch: 'challenge', cost: 55, grants: { kind: 'difficulty', difficulty: 'hard' } },
    { id: 'overload.critical', name: 'Overload: Critical', branch: 'challenge', cost: 65, grants: { kind: 'overload', tier: 3 } },
    { id: 'overload.meltdown', name: 'Overload: Meltdown', branch: 'challenge', cost: 100, prereq: 'overload.critical', grants: { kind: 'overload', tier: 4 } },
    { id: 'theme.amber', name: 'Amber Terminal', branch: 'cosmetic', cost: 30, grants: { kind: 'theme', theme: 'amber' } },
    { id: 'theme.phosphor', name: 'Phosphor Green', branch: 'cosmetic', cost: 30, grants: { kind: 'theme', theme: 'phosphor' } },
  ],

  // Flux meta-currency tunables — see docs/GAME_DESIGN.md §19.1.
  fluxPerWave: 4,
  fluxScoreDivisor: 500,
  fluxWinBonus: 20,
  difficultyFluxMult: { easy: 0.8, normal: 1.0, hard: 1.4 },
  modeFluxMult: { campaign: 1.0, endless: 0.6, maze: 0.8 },
  runHistoryMax: 20,
  profileKey: 'overvolt.profile',

  highScoreKey: 'overvolt.highscore',
  endlessHighScoreKey: 'overvolt.endless.highscore',
  endlessBestWaveKey: 'overvolt.endless.bestWave',
  mazeHighScoreKey: 'overvolt.maze.highscore',
  mazeBestWaveKey: 'overvolt.maze.bestWave',

  colors: {
    background: '#0a0e1a',
    grid: '#1b2740',
    pathFill: '#14304a',
    pathGlow: '#2de2e6',
    spawn: '#2de2e6',
    core: '#ff5ca8',
    hudBg: '#0c1322',
    hudText: '#e8f0ff',
    hudMuted: '#7d8bab',
    accent: '#2de2e6',
    warning: '#ff8c42',
    rangeRing: '#2de2e6',
    good: '#6cf2a0',
    bad: '#ff4d6d',
    cryo: '#7ad7ff',
    frost: '#7ad7ff',
    mortar: '#ff5630',
    sniper: '#ff2e88',
    corrode: '#b6ff3a',
    emp: '#bfe6ff',
    patch: '#27d3a8',
    kernel: '#ff2d55',
  },
};
